import React from 'react'
import { CSSObject } from '@emotion/react'

import {
  AlignItems,
  Box,
  Color,
  colors,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Heading1,
  JustifyContent,
  Overflow,
  Text,
  TextAlign,
  VisuallyHidden,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { useMarketingPricingConfig } from '@library/api-pricing'

import { CWCAppearance } from '../CWCAppearance/CWCAppearance'
import {
  CWCDiscountPills,
  HERO_DISCOUNT_CONTAINER_WIDTH,
} from '../CWCDiscountPills/CWCDiscountPills'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { CWCNewBadge } from '../CWCNewBadge/CWCNewBadge'
import { CWCHeroBackground } from './components/CWCHeroBackground/CWCHeroBackground'
import { DiscountBox } from '../AboveTheFoldSection/components/DiscountBanner/config'
import { LandingPageContentSections, trackCTAClick, TrackingActions } from '../../tracking'

export type CWCHeroProps = {
  discountBoxes: DiscountBox[]
  /** Sign-up destination the CTA links to (the anchor `href` — a real navigation target and no-JS fallback). */
  signupUrl: string
  /**
   * Shared signup handler (applies the promo code, then redirects). The CTA is a real `<a href>`, so
   * the click prevents the raw anchor navigation and dispatches this instead — matching the What's New
   * and First Box CTAs so the promo is applied consistently.
   */
  onCTAClick: () => void
}

// 4-value responsive arrays map to [base(<640), 640+, 768+ (tablet), 1024+ (desktop)].
// Tablet (768–1023) is a distinct band from desktop (≥1024) — a 3-value array would
// lump every width ≥768 onto the desktop value. See AboveTheFoldSection for the convention.

// Headline — ExtraBold. Brand sizes: 40/44 mobile, 56/64 tablet (≥768), 72/80 desktop (≥1024), via
// responsiveStyle (a plain fontSize loses to Citrus's own @media default). Below 700px height AND
// mobile width drops to 28px/36px (Figma's SE reference) — width-gated so a short desktop window
// doesn't get shrunk too. `700px` can't be lowered: `CWCHeroBackground.tsx`'s formulas assume it.
const Headline = withCitrusTheme(Heading1, (theme) => [
  { fontWeight: 800, marginTop: '10px' } as CSSObject,
  theme.responsiveStyle(['40px', '40px', '56px', '72px'], ['fontSize']) as CSSObject,
  theme.responsiveStyle(['44px', '44px', '64px', '80px'], ['lineHeight']) as CSSObject,
  {
    '@media (max-height: 700px) and (max-width: 639px)': {
      fontSize: '28px',
      lineHeight: '36px',
    },
  } as CSSObject,
  // Desktop short-viewport exception, paired with `ContentWrapper`'s own `900px` exception below —
  // drops to tablet's own 56px/64px size (not invented) below 900px so the header-to-banner gap can
  // stay visible instead of fully collapsing on short real windows. See tasks.md task 1.7.
  {
    '@media (max-height: 900px) and (min-width: 1024px)': {
      fontSize: '56px',
      lineHeight: '64px',
    },
  } as CSSObject,
])

// Responsive line-break helpers for the headline. Rendered as <span> (valid phrasing content
// inside an <h1>) toggled block/none per breakpoint — a display:block span forces the wrap. A
// Box here would emit a <div>, which is invalid inside a heading and risks SSR hydration splits.
// The 640–767 band is still mobile (tablet only starts at 768), so it keeps the mobile three-line
// wrap; the tablet two-line wrap is confined to index 2 (768–1023). The arrays must switch at 768,
// not 640, to stay consistent with the mobile headline font/layout in that band.
const BreakMobileDesktop = withCitrusTheme('span', (theme) =>
  theme.responsiveStyle(['block', 'block', 'none', 'block'], ['display']),
)
const BreakTablet = withCitrusTheme('span', (theme) =>
  theme.responsiveStyle(['none', 'none', 'block', 'none'], ['display']),
)

// Content column. Desktop (Figma 183:82): 128px left padding and an 80px gap between the heading
// group and the offer group; 80px isn't expressible as a SpaceScale marginer so it's set here.
// Mobile/tablet keep 24px side padding.
//
// Mobile (<640px) gap is adaptive via space-between + flex-grow below (no fixed `gap`), growing with
// `HeroRoot`'s height so the background image scales with it instead of floating in empty space. No
// landscape override here — short viewports are allowed to overflow rather than compress.
const ContentWrapper = withCitrusTheme(Box, (theme) => [
  // Desktop's 80px gap stays fixed at every *common* viewport height (Alicia's call) — unlike
  // mobile/tablet's adaptive `space-between` gap. Extra room from `HeroRoot`'s `min-height` goes into
  // `justifyContent: Center`'s margin around the two-group block instead of stretching this gap. The
  // short-viewport override below is the deliberate exception — see its own comment and tasks.md task 1.
  theme.responsiveStyle([null, '0px', '0px', '80px'], ['gap']) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '24px', '128px'], ['paddingLeft']) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '24px', '24px'], ['paddingRight']) as CSSObject,
  // Top/bottom padding — moved here from `paddingV`/`paddingBottom` JSX props (Citrus's own
  // SpaceScale-indexed prop system) so it lives alongside `gap`/`paddingLeft`/`paddingRight` as a
  // single source of truth. Tablet (index 2) top is 64px per Figma 183:136; bottom keeps 40px
  // (paddingV would otherwise apply 64px symmetrically). Desktop (index 3) is 80px top/bottom — the
  // platform's desktop section-padding standard, matching Figma's own composition (node 1922:2068) at
  // common viewport heights. The short-viewport exception below tightens it further — see its comment.
  theme.responsiveStyle(['32px', '40px', '64px', '80px'], ['paddingTop']) as CSSObject,
  theme.responsiveStyle(['32px', '40px', '40px', '80px'], ['paddingBottom']) as CSSObject,
  // Desktop short-viewport exception (below 900px): tightens the header-to-CTA gap to 40px and
  // padding to 56px (the platform's mobile/tablet standard, not an invented number) so short real
  // windows keep some visible gap and next-section peek instead of Figma's fixed 80px gap collapsing
  // to 0px. `900px` sits just above where `min-height: max(85vh, 768px)`'s `85vh` term crosses the
  // unmodified 768px floor (~904px), so `HeroRoot`'s matching override below switches over at the
  // same point. Full derivation across three rounds of live sign-off: tasks.md tasks 1.6-1.7.
  {
    '@media (max-height: 900px) and (min-width: 1024px)': {
      gap: '40px',
      paddingTop: '56px',
      paddingBottom: '56px',
    },
  } as CSSObject,
])

// Eyebrow "Meet the NEW Gousto" — Axiforma Bold. Brand sizes: 17/17.85 mobile, 24/32 tablet (≥768),
// 28/36 desktop (≥1024).
const EyebrowText = withCitrusTheme(Text, (theme) => [
  { fontWeight: 700 } as CSSObject,
  theme.responsiveStyle(['17px', '17px', '24px', '28px'], ['fontSize']) as CSSObject,
  theme.responsiveStyle(['17.85px', '17.85px', '32px', '36px'], ['lineHeight']) as CSSObject,
])

// Wraps the shared CWCLinkButton to reproduce the spacing/width the previous bespoke CTALink carried
// itself: a top margin between the discount pills and the CTA, and the 100%→345px responsive width.
// The button fills this wrapper (its own width is 100% up to the 345px cap), so framing is preserved.
const CTAWrapper = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(['12px', '18px', '18px', '18px'], ['marginTop']) as CSSObject,
  theme.responsiveStyle(['100%', '345px', '345px', '345px'], ['width']) as CSSObject,
  // Mobile-only cap — B20-1278, shares `CWCDiscountPills`' own width constant so the CTA can't drift
  // out of sync with the discount banner it lines up with (they'd previously hardcoded `345px` and
  // `342px` independently — a real bug fixed under B20-1278). `max-width` (not fixed) so it still
  // shrinks below that on screens narrower than it, e.g. iPhone SE.
  theme.responsiveStyle(
    [HERO_DISCOUNT_CONTAINER_WIDTH, null, null, null],
    ['maxWidth'],
  ) as CSSObject,
])

const BenefitsContainer = withCitrusTheme(Box, (theme) => ({
  ...(theme.responsiveStyle(['10px', '12px', '12px'], ['marginTop']) as CSSObject),
}))

const PriceText = withCitrusTheme(Text, (theme) => ({
  color: theme.color(Color.White),
}))

// Dark fallback behind the background image, matching its top edge colour where tablet's shifted
// image leaves a strip uncovered. Mobile/desktop images cover the hero fully.
//
// Mobile height leaves ~15% of viewport for the next section to peek through (Alicia's call). The
// `570px` floor still matters below ~671px height: pure `85vh` would undercut content there and
// drop the CTA below the fold — Figma's own smallest "iPhone SE" frame overflows the same way at
// that size rather than fitting everything above the fold.
const HeroRoot = withCitrusTheme(Box, (theme) => [
  { backgroundColor: '#20130b' } as CSSObject,
  // `svh` (small viewport height) is sized for the browser's *maximum* chrome state — unlike `dvh`,
  // it does not live-track the toolbar as it auto-hides/shows during scroll, so it can't desync the
  // background image formula or reflow the CTA/discount group mid-gesture (see design.md). It gives
  // the same never-overflow guarantee `dvh` gives at load (both ship together, same browser support),
  // just without `dvh`'s live-tracking side effect this Hero doesn't want.
  {
    minHeight: 'max(85vh, 570px)',
    '@supports (height: 100svh)': { minHeight: 'max(85svh, 570px)' },
  } as CSSObject,
  theme.responsiveStyle([null, '812px', '1000px', 'auto'], ['height', 'minHeight']) as CSSObject,
  // Landscape override (real phones are often >=640px wide once rotated, so this can't nest inside
  // the mobile-only rule). Resets `height` to `auto`, since an explicit `height` above always wins
  // over a smaller `min-height`. `max-width: 1023px` excludes short desktop/tablet windows (which
  // should keep their own height treatment above — tablet's fixed `1000px`, desktop's `auto`) while
  // still covering real landscape phone widths.
  {
    '@media (max-height: 500px) and (max-width: 1023px)': {
      height: 'auto',
      minHeight: 'max(70vh, 570px)',
      '@supports (height: 100svh)': { minHeight: 'max(70svh, 570px)' },
    },
  } as CSSObject,
  // Desktop: mirrors mobile's own viewport-relative-floor mechanism rather than a fixed-height-only
  // approach, which verified fine against Figma-assumed viewport heights but left the CTA below the
  // fold on a real browser's shorter usable height (real toolbar/bookmarks-bar chrome) — see tasks.md
  // task 1.5. `768px` is content's own measured natural height; `height: auto` above already
  // guarantees Hero is never smaller than that. `85vh` matches mobile's ~15% "next section peeks
  // through" margin (Alicia's call).
  {
    '@media (min-width: 1024px)': {
      minHeight: 'max(85vh, 768px)',
    },
  } as CSSObject,
  // Paired with `ContentWrapper`'s/`Headline`'s own `900px` exceptions above: once the headline
  // shrinks and the gap halves there, content's natural minimum drops from 768px — the floor here
  // must drop to match, or `HeroRoot` keeps reserving room for the *untightened* content and those
  // exceptions give nothing back. (Value verified live against the running app, not hand-derived.)
  {
    '@media (min-width: 1024px) and (max-height: 900px)': {
      minHeight: 'max(85vh, 630px)',
    },
  } as CSSObject,
])

export function CWCHero({ discountBoxes, signupUrl, onCTAClick }: CWCHeroProps) {
  const defaultPricing = { pricePerPortion: '3.20', discountedPricePerPortion: '1.60' }
  const pricing = useMarketingPricingConfig()
  const pricePerPortion = pricing?.pricePerPortion ?? defaultPricing.pricePerPortion
  const discountedPricePerPortion =
    pricing?.discountedPricePerPortion ?? defaultPricing.discountedPricePerPortion

  const handleSignupClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // The CTA is a real <a href>, so prevent the raw anchor navigation and dispatch the shared
    // signup handler instead (applies the promo, then redirects) — matching the What's New CTA.
    event.preventDefault()
    onCTAClick()
    trackCTAClick(TrackingActions.CLICK_GET_STARTED, LandingPageContentSections.HERO)
  }

  return (
    <HeroRoot
      data-testid="hero"
      position={DisplayPosition.Relative}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      // Mobile and desktop: `ContentWrapper` needs `HeroRoot` to be a flex column so it can fill it
      // via `flex-grow` (see `ContentWrapper`'s own comment) — B20-1278/B20-1277. Tablet keeps its
      // own fixed-height + `height: 100%` mechanism instead (doesn't need flex). Height/minHeight for
      // every breakpoint live on the styled-component CSS above, not as JSX props here, so there's a
      // single source of truth per breakpoint.
      display={[Display.Flex, null, null, Display.Flex]}
      flexDirection={[FlexDirection.Column, null, null, FlexDirection.Column]}
    >
      <CWCHeroBackground />
      <ContentWrapper
        data-testid="heroContent"
        position={DisplayPosition.Relative}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        // Mobile/tablet: SpaceBetween pins the header group top / offer group bottom, with an
        // adaptive gap between them (their own fixed-height mechanism) — B20-1278. Desktop: Center,
        // not SpaceBetween — Alicia's call: the 80px distance between the header group and the
        // banner+CTA group must stay visually consistent across desktop viewport heights, so any
        // extra room `HeroRoot`'s `min-height` gives goes into margin above/below the two-group
        // block (via Center), not into stretching the gap `ContentWrapper` fixes at 80px.
        justifyContent={[
          JustifyContent.SpaceBetween,
          JustifyContent.SpaceBetween,
          JustifyContent.SpaceBetween,
          JustifyContent.Center,
        ]}
        alignItems={[AlignItems.Center, AlignItems.Center, AlignItems.Center, AlignItems.FlexStart]}
        // Mobile and desktop (base/index 3): `flex-grow` (not `height: 100%`) fills `HeroRoot`'s
        // flex-column box — B20-1278/B20-1277, see design.md Decision 5. `HeroRoot` has no explicit
        // `height` at either breakpoint (only `min-height`, indefinite for CSS layout purposes for an
        // in-flow percentage-height child), so `flex-grow` is what gives `justifyContent` (SpaceBetween
        // or Center) real room to work with once Hero grows past content's natural size. Tablet keeps
        // `height: 100%` since its `HeroRoot` has a real fixed height (`1000px`) to resolve a
        // percentage against.
        flexGrow={[1, null, null, 1]}
        // Explicit 'auto' at the desktop slot, not null — a null slot doesn't reset a Citrus
        // responsive-array prop, it lets the previous breakpoint's value keep cascading forward. See
        // tasks.md task 1.8 for why the leaked `100%` was harmless today but worth fixing anyway.
        height={[null, '100%', '100%', 'auto']}
        // Top/bottom padding lives on `ContentWrapper`'s own styled-component CSS now, alongside
        // `paddingLeft`/`paddingRight` — not as `paddingV`/`paddingBottom` props here — so there's a
        // single source of truth per breakpoint.
      >
        <Box
          width="100%"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={[
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.FlexStart,
          ]}
        >
          <Box display={Display.Flex} alignItems={AlignItems.Center} gap={0.75}>
            {/* The sticker itself is decorative (aria-hidden) — "New" is kept as real,
                visually-hidden text so assistive tech still hears "Meet the New Gousto",
                matching what this eyebrow said before the sticker replaced the literal word. */}
            <EyebrowText color={Color.White}>Meet the </EyebrowText>
            <VisuallyHidden>New </VisuallyHidden>
            {/* The badge animates in on page load (above the fold, nothing to scroll to).
                inline-flex keeps the wrapper from adding a line-box/descender gap that would shift
                the badge within the eyebrow row. */}
            <CWCAppearance trigger="load" display="inline-flex">
              <CWCNewBadge width={['62px', null, '67px']} height={['44px', null, '48px']} />
            </CWCAppearance>
            <EyebrowText color={Color.White}>Gousto</EyebrowText>
          </Box>
          <Headline
            color={Color.White}
            fontFamily={FontFamily.ExtraBold}
            textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
          >
            {/* Responsive line breaks: mobile/desktop wrap "The recipe box / that works / around
                you", tablet wraps "The recipe box that / works around you" (design 183:141). Spans
                toggle block (break) or none per breakpoint — valid inside an <h1>. */}
            {'The recipe box'}
            <BreakMobileDesktop />
            {' that '}
            <BreakTablet />
            {'works'}
            <BreakMobileDesktop />{' '}
            <span style={{ color: colors[Color.Primary_600] }}>around you</span>
          </Headline>
        </Box>
        <Box
          width="100%"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          alignItems={[
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.Center,
            AlignItems.FlexStart,
          ]}
        >
          <CWCDiscountPills
            discountBoxes={discountBoxes}
            price={{ pricePerPortion, discountedPricePerPortion }}
          />
          <CTAWrapper>
            {/* fontSizeOverride restores the Hero CTA's Figma-verified 18px label — CWCLinkButton's
                primary variant defaults to 16px. Line-height isn't overridden: the label is a single
                line, flex-centred in the 48px-min-height button, so the old 28px line-height had no
                visual effect here. data-testing is the above-the-fold hook the promo/RAF regression
                journeys click; it must be on the CwC hero CTA now that CwC is the default homepage. */}
            <CWCLinkButton
              href={signupUrl}
              onClick={handleSignupClick}
              fontSizeOverride="18px"
              data-testing="homepageAboveTheFoldCTA"
            >
              Get started
            </CWCLinkButton>
          </CTAWrapper>
          <BenefitsContainer
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            width={['100%', '400px', '400px']}
          >
            <PriceText
              size={2}
              textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
            >
              <span style={{ fontWeight: FontWeight.Bold }}>Free next-day delivery</span> on your
              1st box<span style={{ fontSize: '9px', verticalAlign: 'super' }}>†</span>
            </PriceText>
            <PriceText
              size={2}
              textAlign={[TextAlign.Center, TextAlign.Center, TextAlign.Center, TextAlign.Left]}
            >
              <span style={{ fontWeight: FontWeight.Bold }}>Skip or cancel</span> for free anytime
            </PriceText>
          </BenefitsContainer>
        </Box>
      </ContentWrapper>
    </HeroRoot>
  )
}
